import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import SelectChevron from '@/components/base/SelectChevron';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@/components/feature/TurnstileWidget';
import { verifyTurnstileToken } from '@/lib/turnstile';

const MAX_MESSAGE_LENGTH = 500;

export default function PlanMyTripForm() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [message, setMessage] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Anti-spam: if the hidden field is filled, silently pretend success.
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessage('');
      setCharCount(0);
      return;
    }

    if (!turnstileToken) {
      setStatus('error');
      setFormError(t('ss.captchaRequired'));
      return;
    }

    const captchaOk = await verifyTurnstileToken(turnstileToken);
    if (!captchaOk) {
      setStatus('error');
      setFormError(t('ss.captchaFailed'));
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      return;
    }

    setStatus('loading');
    setFormError('');

    try {
      const payload = {
        name: String(formData.get('name') || ''),
        email: String(formData.get('email') || ''),
        phone: String(formData.get('phone') || ''),
        address: String(formData.get('address') || ''),
        destination: String(formData.get('destination') || ''),
        travel_date: String(formData.get('travel_date') || ''),
        group_size: String(formData.get('group_size') || ''),
        budget: String(formData.get('budget') || ''),
        message: String(formData.get('message') || ''),
        website_alt: String(formData.get('website_alt') || ''),
      };

      const { data, error } = await supabase.functions.invoke('vietnamtravel-send-inquiry', {
        body: payload,
      });

      if (error) throw error;

      if (!data || data.ok !== true) {
        throw new Error((data && data.error) || t('ss.sendFailed'));
      }

      setStatus('success');
      setFormError('');
      form.reset();
      setMessage('');
      setCharCount(0);
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } catch (err) {
      setStatus('error');
      setFormError(
        err instanceof Error ? err.message : t('ss.networkError')
      );
    }
  };

  return (
    <section id="plan-my-trip" className="w-full bg-white py-16 md:py-24 scroll-mt-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left info */}
          <div className="lg:sticky lg:top-28">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {t('ss.planTitleA')}{' '}
              <span className="text-primary-500">{t('ss.planTitleB')}</span>
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-8">
              {t('ss.planSubtitle')}
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-timer-line text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground-950">{t('ss.replyTitle')}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t('ss.replyDesc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                  <i className="ri-wallet-3-line text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground-950">{t('ss.pricingTitle')}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t('ss.pricingDesc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-loop-left-line text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground-950">{t('ss.adjustTitle')}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t('ss.adjustDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-xl border border-background-200/70 p-6 md:p-8">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
              {t('ss.formTitle')}
            </h3>
            <p className="text-sm text-foreground-600 mb-6">
              {t('ss.formSubtitle')}
            </p>

            <form onSubmit={handleSubmit} data-readdy-form noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="plan-name" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('ss.fullName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="plan-name"
                    name="name"
                    type="text"
                    required
                    placeholder={t('ss.yourName')}
                    className="w-full rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="plan-email" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('ss.email')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="plan-email"
                    name="email"
                    type="email"
                    required
                    placeholder={t('ss.emailPlaceholder')}
                    className="w-full rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="plan-phone" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('ss.phone')}
                  </label>
                  <input
                    id="plan-phone"
                    name="phone"
                    type="tel"
                    placeholder={t('ss.phonePlaceholder')}
                    className="w-full rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="plan-address" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('ss.fullAddress')}
                  </label>
                  <input
                    id="plan-address"
                    name="address"
                    type="text"
                    placeholder={t('ss.addressPlaceholder')}
                    className="w-full rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="plan-destination" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('ss.destination')}
                  </label>
                  <div className="relative">
                    <select
                      id="plan-destination"
                      name="destination"
                      className="w-full appearance-none rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 cursor-pointer"
                    >
                      <option value="Not sure yet">{t('ss.destNotSure')}</option>
                      <option value="Northern Vietnam (Hanoi, Sapa, Ha Long)">{t('ss.destNorth')}</option>
                      <option value="Central Vietnam (Hoi An, Hue, Da Nang)">{t('ss.destCentral')}</option>
                      <option value="Southern Vietnam (HCMC, Mekong Delta)">{t('ss.destSouth')}</option>
                      <option value="Whole Vietnam">{t('ss.destWhole')}</option>
                    </select>
                    <SelectChevron className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                  </div>
                </div>

                <div>
                  <label htmlFor="plan-date" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('ss.travelDate')}
                  </label>
                  <input
                    id="plan-date"
                    name="travel_date"
                    type="date"
                    className="w-full rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="plan-group" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('ss.groupSize')}
                  </label>
                  <div className="relative">
                    <select
                      id="plan-group"
                      name="group_size"
                      className="w-full appearance-none rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 cursor-pointer"
                    >
                      <option value="1 (solo)">{t('ss.groupSolo')}</option>
                      <option value="2 (couple)">{t('ss.groupCouple')}</option>
                      <option value="3 - 5">{t('ss.group3to5')}</option>
                      <option value="6 - 10">{t('ss.group6to10')}</option>
                      <option value="10+">{t('ss.group10plus')}</option>
                    </select>
                    <SelectChevron className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="plan-budget" className="block text-sm font-medium text-secondary-700 mb-1.5">
                    {t('ss.budget')}
                  </label>
                  <div className="relative">
                    <select
                      id="plan-budget"
                      name="budget"
                      className="w-full appearance-none rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 cursor-pointer"
                    >
                      <option value="Under $500">{t('ss.budgetUnder500')}</option>
                      <option value="$500 - $1,000">{t('ss.budget500to1000')}</option>
                      <option value="$1,000 - $2,000">{t('ss.budget1000to2000')}</option>
                      <option value="$2,000+">{t('ss.budget2000plus')}</option>
                    </select>
                    <SelectChevron className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="plan-message" className="block text-sm font-medium text-secondary-700 mb-1.5">
                  {t('ss.travelIdeas')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="plan-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={MAX_MESSAGE_LENGTH}
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    setCharCount(e.target.value.length);
                  }}
                  placeholder={t('ss.messagePlaceholder')}
                  className="w-full rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-none"
                />
                <div className="flex justify-end mt-1">
                  <span className="text-xs text-secondary-400">
                    {charCount}/{MAX_MESSAGE_LENGTH}
                  </span>
                </div>
              </div>

              {/* Anti-spam honeypot — do not remove */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="vts-field-check"
              />

              <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-full transition-colors whitespace-nowrap"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    {t('ss.sending')}
                  </>
                ) : (
                  <>
                    {t('ss.planMyTrip')}
                    <i className="ri-send-plane-line" />
                  </>
                )}
              </button>

              {status === 'success' && (
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 p-4">
                  <i className="ri-checkbox-circle-line text-green-500 shrink-0" />
                  <p className="text-sm text-green-700">
                    {t('ss.success')}
                  </p>
                </div>
              )}

              {status === 'error' && (
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-4">
                  <i className="ri-error-warning-line text-red-500 shrink-0" />
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}